import Link from 'next/link';
import { Box } from 'theme-ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styles from '@features/cms/components/collections/ProductPremiumBlock/components/ProductPremiumArrangement/ProductPremiumArrangement.styles';
import ProductPremiumImage from '@features/cms/components/collections/ProductPremiumBlock/components/ProductPremiumImage/ProductPremiumImage';
import { IProductPremiumArrangementVariantBlock, IProductPremiumSingleBlock, TProductPremiumSingleBlockWitParsedImage } from '@features/cms/typescript';
import useProductPremiumSlide from '@hooks/useProductPremiumSlide';
import { IParsedImage } from '@components/typescript';
import Button from '@components/Button/Button';
import getProductUrl from '@utils/getProductUrl';
import ProductState from '@features/categories/mobx/state/ProductState';
import useResponsive from '@features/responsive/hooks/useResponsive';
import { useOffer } from '@hooks/useOffer';
import Heading from '@components/Heading/Heading';
import stackedStyles from '@features/cms/components/collections/ProductPremiumBlock/ProductPremiumStacked.styles';
type PropsType = {
  blocks: TProductPremiumSingleBlockWitParsedImage[];
  activeBlock: number;
  viewportHeight: number;
  viewportWidth: number;
  isFirst: boolean;
  isStackedMobile?: boolean;
};
const ProductPremiumArrangement = ({
  blocks,
  activeBlock,
  viewportHeight,
  viewportWidth,
  isFirst,
  isStackedMobile = false
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const contentRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef<HTMLDivElement>(null);
  const mainImageRef = useRef<HTMLDivElement>(null);
  const productImageContainerRef = useRef<HTMLDivElement>(null);
  const [productImageHeight, setProductImageHeight] = useState(0);
  const [mainImageHeight, setMainImageHeight] = useState(0);
  const {
    isMobile: isMobileSmall,
    isMobilelg,
    isTablet: isTabletSmall,
    isTabletlg
  } = useResponsive();
  const isMobile = isMobileSmall || isMobilelg;
  const isTablet = isTabletSmall || isTabletlg;
  const {
    previousBlock,
    currentBlock
  } = useProductPremiumSlide(blocks, activeBlock);
  const [blockData, setBlockData] = useState<IProductPremiumSingleBlock<IProductPremiumArrangementVariantBlock, IParsedImage>>(currentBlock as IProductPremiumSingleBlock<IProductPremiumArrangementVariantBlock, IParsedImage>);
  const block = currentBlock as IProductPremiumSingleBlock<IProductPremiumArrangementVariantBlock, IParsedImage>;
  const {
    arrangementProductImage
  } = blockData.variant;
  const productObject = blockData.objectType === 'Product' ? new ProductState(blockData.object) : null;
  const [_, priceListName] = useOffer();
  const recalculateProductImageHeight = () => {
    const heightSpacings = isMobile ? 32 : isTablet ? 64 : 0;
    const topMargin = isMobile ? 16 : isTablet ? 160 : 0;
    const leftMargin = isMobile ? 40 : isTablet ? 64 : 0;
    const rightMargin = isMobile ? 64 : isTablet ? 80 : 0;
    const availableViewportWidth = viewportWidth - leftMargin - rightMargin;
    const contentHeight = contentRef.current?.offsetHeight || 0;
    const availableHeight = viewportHeight - topMargin - mainImageHeight - contentHeight - heightSpacings;
    const calcProductImageHeight = availableViewportWidth / 4 * 3;
    if (productImageContainerRef.current && contentRef.current && slideRef.current) {
      if (isMobile || isTablet) {
        productImageContainerRef.current.style.height = 'auto';
        slideRef.current.style.height = '100%';
      } else {
        const calcHeight = contentRef.current.offsetWidth / 4 * 3;
        productImageContainerRef.current.style.height = `${calcHeight}px`;
        const mainImageWidth = mainImageRef.current?.offsetWidth || 0;
        const calcSlideHeight = mainImageWidth / 4 * 3;
        slideRef.current.style.height = `${calcSlideHeight}px`;
      }
    }
    if (calcProductImageHeight > availableHeight) {
      return availableHeight;
    }
    return calcProductImageHeight;
  };
  const recalculateMainImageHeight = () => {
    const leftMargin = isMobile ? 0 : isTablet ? 64 : 0;
    const rightMargin = isMobile ? 0 : isTablet ? 80 : 0;
    const availableViewportWidth = viewportWidth - leftMargin - rightMargin;
    const calcMainImageHeight = availableViewportWidth / 4 * 3;
    return calcMainImageHeight;
  };
  const recalculateContentWidth = () => {
    if (!contentRef.current) return;
    if (isTablet) {
      const calculatedContentWidth = productImageHeight / 3 * 4;
      contentRef.current.style.width = `${calculatedContentWidth}px`;
    } else {
      contentRef.current.style.width = '100%';
    }
  };
  useEffect(() => {
    const nextMainImageHeight = recalculateMainImageHeight();
    const nextProductImageHeight = recalculateProductImageHeight();
    setMainImageHeight(nextMainImageHeight > 0 ? nextMainImageHeight : viewportWidth / 4 * 3);
    setProductImageHeight(nextProductImageHeight > 0 ? nextProductImageHeight : viewportWidth / 4 * 3);
    const timeoutId = window.setTimeout(() => {
      recalculateContentWidth();
    }, 200);
    return () => window.clearTimeout(timeoutId);
  }, [viewportHeight, viewportWidth, isMobile, isTablet]);
  useEffect(() => {
    const nextMainImageHeight = recalculateMainImageHeight();
    const nextProductImageHeight = recalculateProductImageHeight();
    setMainImageHeight(nextMainImageHeight > 0 ? nextMainImageHeight : viewportWidth / 4 * 3);
    setProductImageHeight(nextProductImageHeight > 0 ? nextProductImageHeight : viewportWidth / 4 * 3);
    recalculateContentWidth();
    if (contentRef.current && productImageContainerRef.current) {
      contentRef.current.style.opacity = '1';
    }
  }, [blockData]);
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.opacity = '0.5';
    }
    setTimeout(() => {
      setBlockData({
        ...block
      });
    }, 200);
  }, [currentBlock]);
  if (isStackedMobile) {
    const stackedBlock = blocks[0] as IProductPremiumSingleBlock<IProductPremiumArrangementVariantBlock, IParsedImage>;
    const stackedProductObject = stackedBlock.objectType === 'Product' ? new ProductState(stackedBlock.object) : null;
    if (!stackedProductObject) return null;
    const {
      name: stackedName,
      id: stackedId,
      getPrice,
      getPromoPrice
    } = stackedProductObject;
    const stackedProductPrice = getPromoPrice(priceListName) || getPrice(priceListName);
    return <div sx={stackedStyles.slide} data-testid="product-premium-arrangement-slide">
        <div sx={stackedStyles.mainImageProduct} data-testid="product-premium-arrangement-main-image">
          <ProductPremiumImage image={stackedBlock.image} video={stackedBlock.video} fullScreen />
        </div>
        <div sx={stackedStyles.contentProduct} data-testid="product-premium-arrangement-content">
          <div sx={styles.arrangementTitleContainer}>
            {isFirst ? <Heading h={1}>{stackedName}</Heading> : <p>{stackedName}</p>}
          </div>
          <div sx={styles.arrangementPriceContainer} data-testid="product-premium-arrangement-price-container">
            <p className="price small">{stackedProductPrice}</p>
          </div>
          <Link href={`${getProductUrl(stackedName, stackedId)}`} aria-label={stackedName}>
            <Button variant="outlined" variantColor="dark" size="small" addSx={styles.arrangementProductButton} data-testid="product-premium-arrangement-button">
              {t('buyNow')}
            </Button>
          </Link>
        </div>
      </div>;
  }
  if (!productObject) return null;
  const {
    name,
    id,
    getPrice,
    getPromoPrice
  } = productObject;
  const productPrice = getPromoPrice(priceListName) || getPrice(priceListName);
  return <div sx={styles.sliderGrid}>
      <Box sx={styles.descriptionVariantContainer} ref={slideRef}>
        <div sx={styles.descriptionVariantWrapper(previousBlock.position, currentBlock.position)}>
          <div sx={styles.productDescriptionImageContainer} ref={productImageContainerRef}>
            <ProductPremiumImage image={arrangementProductImage} video={{
            thumbnail: '',
            link: ''
          }} height={productImageHeight} alignLeft />
          </div>
          <div sx={styles.contentContainer} ref={contentRef}>
            <div>
              <div sx={styles.arrangementTitleContainer}>
                {isFirst ? <Heading h={1}>{name}</Heading> : <p>{name}</p>}
              </div>
              <div sx={styles.arrangementPriceContainer}>
                <p className="price small">{productPrice}</p>
              </div>
              <Link href={`${getProductUrl(name, id)}`} aria-label={name}>
                <Button variant="outlined" variantColor="dark" size="small" addSx={styles.arrangementProductButton}>
                  {t('buyNow')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Box>
      <div sx={styles.mainImageContainer}>
        <ProductPremiumImage image={currentBlock.image} video={currentBlock.video} height={mainImageHeight} fullScreen={!!isMobile} ref={mainImageRef} />
      </div>
    </div>;
};
export default ProductPremiumArrangement;